import { useEffect, useMemo, useState } from "react";

function parseStartTime(startTime) {
  if (!startTime) return null;
  const parsed = new Date(startTime);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function elapsedParts(startTime, nowMs = Date.now()) {
  const start = parseStartTime(startTime);
  if (!start) {
    return { totalSeconds: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const totalSeconds = Math.max(0, Math.floor((nowMs - start.getTime()) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { totalSeconds, hours, minutes, seconds };
}

export function formatElapsedClock(parts) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(parts.hours)}:${pad(parts.minutes)}:${pad(parts.seconds)}`;
}

export function formatElapsedVerbose(parts) {
  const chunks = [];
  if (parts.hours > 0) chunks.push(`${parts.hours} hr${parts.hours === 1 ? "" : "s"}`);
  if (parts.minutes > 0 || parts.hours > 0) chunks.push(`${parts.minutes} min${parts.minutes === 1 ? "" : "s"}`);
  chunks.push(`${parts.seconds} sec${parts.seconds === 1 ? "" : "s"}`);
  return chunks.join(" ");
}

/** Live ticking counter from an ISO start_time string. */
export default function RuntimeCounter({
  startTime,
  running = true,
  className = "",
  clockClassName = "font-mono text-3xl sm:text-4xl tabular-nums text-primary",
  showVerbose = true,
  prefix = "Running for",
  stoppedLabel = "Stopped",
  testId,
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!running || !startTime) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running, startTime]);

  const parts = useMemo(() => elapsedParts(startTime, nowMs), [startTime, nowMs]);

  if (!running || !startTime) {
    return (
      <div className={className} data-testid={testId}>
        <div className="font-mono text-2xl tabular-nums text-muted-foreground">00:00:00</div>
        {showVerbose && <p className="text-xs text-muted-foreground mt-1">{stoppedLabel}</p>}
      </div>
    );
  }

  return (
    <div className={className} data-testid={testId}>
      <div className={clockClassName}>{formatElapsedClock(parts)}</div>
      {showVerbose && (
        <p className="text-xs sm:text-sm text-muted-foreground mt-1.5">
          {prefix} {formatElapsedVerbose(parts)} from start
        </p>
      )}
    </div>
  );
}
